import React from 'react';
import { Armchair, ArrowLeftRight, Calculator, Package, ShoppingBag } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

export type ProductConcept = 'glasspos' | 'ledger' | 'befx' | 'iq-store';

export interface ProductPalette {
  base: string;
  key: string;
  fill: string;
  rim: string;
  accent: string;
  reflection: string;
  horizon: string;
}

export interface ProductPresentation {
  id: ProductConcept;
  name: string;
  categoryKey: string;
  assetUrl?: string;
  palette: ProductPalette;
}

export const PRODUCT_PRESENTATIONS: ProductPresentation[] = [
  {
    id: 'glasspos',
    name: 'GlassPOS',
    categoryKey: 'concept.pos.category',
    palette: {
      base: '#071019',
      key: 'rgba(21, 137, 199, 0.38)',
      fill: 'rgba(65, 191, 217, 0.2)',
      rim: 'rgba(114, 208, 229, 0.42)',
      accent: 'rgba(255, 119, 56, 0.32)',
      reflection: 'rgba(31, 145, 188, 0.23)',
      horizon: 'rgba(78, 181, 203, 0.14)',
    },
  },
  {
    id: 'ledger',
    name: 'Raqb',
    categoryKey: 'concept.ledger.category',
    palette: {
      base: '#07110d',
      key: 'rgba(26, 133, 88, 0.32)',
      fill: 'rgba(82, 178, 121, 0.16)',
      rim: 'rgba(122, 211, 155, 0.4)',
      accent: 'rgba(202, 163, 72, 0.21)',
      reflection: 'rgba(35, 130, 91, 0.22)',
      horizon: 'rgba(101, 175, 126, 0.13)',
    },
  },
  {
    id: 'befx',
    name: 'BeFX',
    categoryKey: 'concept.befx.category',
    palette: {
      base: '#0b0918',
      key: 'rgba(104, 74, 211, 0.4)',
      fill: 'rgba(49, 176, 190, 0.18)',
      rim: 'rgba(158, 132, 241, 0.44)',
      accent: 'rgba(38, 196, 178, 0.24)',
      reflection: 'rgba(91, 70, 183, 0.26)',
      horizon: 'rgba(60, 166, 181, 0.14)',
    },
  },
  {
    id: 'iq-store',
    name: 'IQ Store',
    categoryKey: 'concept.store.category',
    palette: {
      base: '#120c09',
      key: 'rgba(172, 82, 43, 0.32)',
      fill: 'rgba(201, 137, 82, 0.16)',
      rim: 'rgba(225, 165, 109, 0.4)',
      accent: 'rgba(103, 145, 112, 0.2)',
      reflection: 'rgba(151, 75, 47, 0.22)',
      horizon: 'rgba(191, 125, 79, 0.13)',
    },
  },
];

/** Hero/cinema presentations limited to products actually published in the catalog. */
export function presentationsForPublishedSlugs(slugs: string[]): ProductPresentation[] {
  const set = new Set(slugs.map((s) => s.toLowerCase()));
  return PRODUCT_PRESENTATIONS.filter((p) => {
    if (p.id === 'ledger') {
      return set.has('ledger') || set.has('raqb') || set.has('raqib');
    }
    if (p.id === 'iq-store') {
      return set.has('iq-store') || set.has('iqstore');
    }
    return set.has(p.id);
  });
}

const MiniBars: React.FC<{ values: number[] }> = ({ values }) => (
  <div className="bc-concept-bars">
    {values.map((value, index) => (
      <span key={`${value}-${index}`} style={{ height: `${value}%` }} />
    ))}
  </div>
);

const GlassPosConcept: React.FC = () => {
  const { t } = useLanguage();
  return (
    <div className="bc-concept-layout bc-concept-pos">
      <aside className="bc-concept-rail">
        <ShoppingBag />
        <span className="is-active" />
        <span />
        <span />
        <span />
      </aside>
      <div className="bc-concept-products">
        <div className="bc-concept-heading">
          <strong>{t('concept.pos.checkout')}</strong>
          <span>09:42</span>
        </div>
        <div className="bc-concept-product-grid">
          {[68, 42, 54, 76, 36, 61].map((tone, index) => (
            <div key={tone} className={index === 1 ? 'is-selected' : ''}>
              <span style={{ opacity: tone / 100 }} />
              <small>#{String(index + 1).padStart(2, '0')}</small>
            </div>
          ))}
        </div>
      </div>
      <aside className="bc-concept-receipt">
        <p>{t('concept.pos.order')}</p>
        {[1, 2, 3].map((item) => (
          <div key={item}>
            <span>{item}×</span>
            <i />
            <b>{(item * 12.5).toFixed(2)}</b>
          </div>
        ))}
        <footer>
          <span>{t('concept.pos.total')}</span>
          <strong>75.00</strong>
        </footer>
      </aside>
    </div>
  );
};

const LedgerConcept: React.FC = () => {
  const { t } = useLanguage();
  return (
    <div className="bc-concept-layout bc-concept-ledger">
      <header className="bc-concept-finance-head">
        <div>
          <Calculator />
          <span>{t('concept.ledger.overview')}</span>
        </div>
        <small>{t('common.today')}</small>
      </header>
      <section className="bc-concept-balance">
        <p>{t('concept.ledger.balance')}</p>
        <strong>284,920.00</strong>
        <span>+8.4%</span>
      </section>
      <section className="bc-concept-chart">
        <div className="bc-concept-chart-grid" />
        <svg viewBox="0 0 320 92" preserveAspectRatio="none" aria-hidden="true">
          <path d="M0,76 C28,68 38,70 61,57 S101,67 126,45 S163,53 187,35 S225,41 245,25 S286,30 320,8" />
          <path
            className="bc-concept-chart-fill"
            d="M0,76 C28,68 38,70 61,57 S101,67 126,45 S163,53 187,35 S225,41 245,25 S286,30 320,8 L320,92 L0,92 Z"
          />
        </svg>
      </section>
      <footer className="bc-concept-finance-metrics">
        <div><span>{t('concept.ledger.income')}</span><strong>+42,680</strong></div>
        <div><span>{t('concept.ledger.expenses')}</span><strong>−18,240</strong></div>
        <MiniBars values={[34, 58, 46, 72, 64, 88]} />
      </footer>
    </div>
  );
};

const BeFxConcept: React.FC = () => {
  const { t } = useLanguage();
  const rates = [
    ['USD', 'IQD', '1,310.00', '1,312.00'],
    ['EUR', 'USD', '1.0842', '1.0851'],
    ['GBP', 'USD', '1.2710', '1.2724'],
  ];
  return (
    <div className="bc-concept-layout bc-concept-fx">
      <header className="bc-concept-fx-head">
        <div><ArrowLeftRight /><span>{t('concept.befx.rates')}</span></div>
        <i />
      </header>
      <section className="bc-concept-rate-focus">
        <div><span>USD</span><strong>10,000</strong></div>
        <ArrowLeftRight />
        <div><span>IQD</span><strong>13,100,000</strong></div>
      </section>
      <section className="bc-concept-rate-table">
        <header>
          <span>{t('concept.befx.pair')}</span>
          <span>{t('concept.befx.buy')}</span>
          <span>{t('concept.befx.sell')}</span>
        </header>
        {rates.map(([from, to, buy, sell]) => (
          <div key={from}>
            <strong>{from}<small> / {to}</small></strong>
            <span>{buy}</span>
            <span>{sell}</span>
          </div>
        ))}
      </section>
    </div>
  );
};

const IqStoreConcept: React.FC = () => {
  const { t } = useLanguage();
  return (
    <div className="bc-concept-layout bc-concept-store">
      <header className="bc-concept-store-head">
        <div><Armchair /><span>{t('concept.store.inventory')}</span></div>
        <small><Package /> 248</small>
      </header>
      <section className="bc-concept-furniture-grid">
        {[
          ['chair', 82],
          ['sofa', 61],
          ['table', 74],
          ['shelf', 43],
        ].map(([shape, stock], index) => (
          <div key={shape} className={index === 0 ? 'is-featured' : ''}>
            <span className={`bc-furniture-shape bc-furniture-${shape}`} />
            <footer>
              <i />
              <small>{stock}%</small>
            </footer>
          </div>
        ))}
      </section>
      <footer className="bc-concept-stock">
        <div><span>{t('concept.store.inStock')}</span><strong>214</strong></div>
        <div><span>{t('concept.store.lowStock')}</span><strong>12</strong></div>
        <MiniBars values={[78, 64, 82, 52, 69, 88, 73]} />
      </footer>
    </div>
  );
};

const conceptComponents: Record<ProductConcept, React.FC> = {
  glasspos: GlassPosConcept,
  ledger: LedgerConcept,
  befx: BeFxConcept,
  'iq-store': IqStoreConcept,
};

export const ProductConceptArtwork: React.FC<{ presentation: ProductPresentation }> = ({
  presentation,
}) => {
  if (presentation.assetUrl) {
    return (
      <img
        src={presentation.assetUrl}
        alt=""
        className="h-full w-full object-cover"
      />
    );
  }

  const Concept = conceptComponents[presentation.id];
  return <Concept />;
};
